/* eslint-disable camelcase */
/** @jsxImportSource @emotion/react */
import { memo, ReactNode, useState, useEffect } from "react";
import { useIntl } from "react-intl";

import { DialogContent, Divider, Typography } from "@material-ui/core";

import { Group, Accordion, RtmpBlock, Button, Label, RadioGroup } from "..";
import { useAuth } from "../../context/auth";
import { useModal } from "../../context/modal";
import { useToast } from "../../context/toast";
import { authFetcher } from "../../hooks/useAuthSWR";
import useCopy from "../../hooks/useCopy";
import useHyperlinksByLocale from "../../hooks/useHyperlinksByLocale";
import { EventStatus, RtmpUrl } from "../../types";

import {
  labelWrapper,
  hint,
  link,
  divider,
  rtmpBlock,
  tokenGenerator,
  tokenContent,
  tokenValue,
  tokenMetadata,
} from "./styles";

// TODO: When integrate with api, remove it
const mockData = {
  enable_multiview: true,
  main_view: {
    channel_id: 1,
    name: "main-view",
    rtmp_url: {
      main: "aaaaa1",
      backup: "bbbbb1",
    },
  },
  sub_view: [
    {
      channel_id: 4,
      name: "view1",
      rtmp_url: {
        main: "aaaaa2",
        backup: "bbbbb2",
      },
    },
    {
      channel_id: 2,
      name: "view2",
      rtmp_url: {
        main: "aaaaa3",
        backup: "bbbbb3",
      },
    },
  ],
};

interface Data {
  rtmp_url?: RtmpUrl;
  enable_multiview?: boolean;
  main_view?: Record<string, any>;
  sub_view?: Array<Record<string, any>>;
}

interface Props {
  className?: string;
  htmlTag?: ReactNode;
  eventId?: string;
  status?: EventStatus;
  disabledRtmpUrl?: boolean;
  data?: Data;
}

const EventLinksGroup = ({
  className,
  htmlTag,
  eventId,
  status,
  disabledRtmpUrl = false,
  data = {},
}: Props): JSX.Element => {
  const intl = useIntl();
  const { RTMP_URL } = useHyperlinksByLocale();
  const { logout } = useAuth();
  const [showModal] = useModal();
  const showToast = useToast();
  const tokenCopy = useCopy();
  const [duration, setDuration] = useState(14);
  const [generating, setGenerating] = useState(false);
  const [expanded, setExpanded] = useState<string | false>(false);
  const tokenGenerationDisabled =
    !status ||
    [
      EventStatus.Encoding,
      EventStatus.ReadyForVOD,
      EventStatus.Vod,
      EventStatus.Ended,
      EventStatus.Canceling,
      EventStatus.Canceled,
      EventStatus.Archiving,
      EventStatus.Archived,
    ].includes(status);

  const generateToken = async (): Promise<void> => {
    if (!eventId || generating || tokenGenerationDisabled) return;
    setGenerating(true);
    try {
      const { token } = await authFetcher<{ token: string }>(`/live-events/${encodeURIComponent(eventId)}/token`, {
        method: "post",
        data: { expires_in_second: duration * 24 * 60 * 60 },
        logout,
      });
      const generatedAt = new Date();
      const expiresAt = new Date(generatedAt.getTime() + duration * 24 * 60 * 60 * 1000);
      const dateFormat = new Intl.DateTimeFormat(intl.locale, {
        timeZone: "Asia/Tokyo",
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "numeric",
        minute: "2-digit",
      });
      showModal({
        title: intl.formatMessage({ id: "event-links.token-generated" }),
        content: (
          <DialogContent css={tokenContent}>
            <div css={labelWrapper()}>
              <span>
                {intl.formatMessage({ id: "event-links.duration" })}:{" "}
                <strong>
                  {duration} {intl.formatMessage({ id: "event-links.days" })}
                </strong>
              </span>
              <Button color="primary" size="small" onClick={(): void => tokenCopy.copy(token)}>
                {intl.formatMessage({ id: "btn.copy" })}
              </Button>
            </div>
            <div css={tokenValue}>{token}</div>
            <p css={hint}>{intl.formatMessage({ id: "event-links.token-hint" })}</p>
            <div css={tokenMetadata}>
              <div>
                <span>{intl.formatMessage({ id: "event-links.generated-at" })}:</span>
                <strong>{dateFormat.format(generatedAt)} (Japan Time)</strong>
              </div>
              <div>
                <span>{intl.formatMessage({ id: "event-links.expires-at" })}:</span>
                <strong>{dateFormat.format(expiresAt)} (Japan Time)</strong>
              </div>
            </div>
          </DialogContent>
        ),
        actions: [
          {
            label: intl.formatMessage({ id: "btn.done" }),
            primary: true,
            onClick: (_event, hideModal): void => hideModal(),
          },
        ],
      });
    } catch (error) {
      showToast(intl.formatMessage({ id: "event-links.token-error" }), { type: "error", variant: "short" });
    } finally {
      setGenerating(false);
    }
  };

  // TODO: When integrate with api, remove it from line 67-71
  const [expandedData, setExpandedData] = useState<Data>({});
  useEffect(() => {
    setExpandedData({ ...data, ...mockData } as Data);
  }, [data]);

  // TODO: When integrate with api, replace expandedData with data from line 74-84
  useEffect(() => {
    if (expandedData?.enable_multiview) {
      setExpanded(expandedData?.main_view?.channel_id);
    }
  }, [expandedData]);

  const enableMultiview = false; // TODO: When enable multiview ui, remove this line and enable line 82;
  const {
    // enable_multiview: enableMultiview,
    main_view: mainView,
    sub_view: subView = [],
  } = expandedData;
  const multiviewItems = enableMultiview ? [mainView, ...subView] : [];

  const handleChange = (panel: string) => (event: any, isExpanded: boolean): void => {
    setExpanded(isExpanded ? panel : false);
  };

  return (
    <Group className={className} title={intl.formatMessage({ id: "event-detail.event-links" })}>
      {eventId && (
        <div css={tokenGenerator}>
          <Label text={intl.formatMessage({ id: "event-links.token-generator" })} />
          <RadioGroup
            name="token-duration"
            radios={[14, 28].map((days) => ({
              label: `${days} ${intl.formatMessage({ id: "event-links.days" })}`,
              value: String(days),
            }))}
            value={String(duration)}
            disabled={generating || tokenGenerationDisabled}
            onChange={(event): void => setDuration(Number(event.target.value))}
          />
          <Button
            variant="contained"
            color="primary"
            disabled={generating || tokenGenerationDisabled}
            onClick={generateToken}
          >
            {intl.formatMessage({ id: "event-links.generate-token" })}
          </Button>
        </div>
      )}
      {htmlTag}
      {htmlTag && enableMultiview && <Divider css={divider} />}
      {enableMultiview ? (
        multiviewItems.map((item) => (
          <Accordion
            title={item?.name}
            content={<RtmpBlock css={rtmpBlock} data={item?.rtmp_url} disabled={disabledRtmpUrl} />}
            isExpanded={expanded === item?.channel_id}
            onChange={handleChange(item?.channel_id)}
          />
        ))
      ) : (
        <RtmpBlock data={data?.rtmp_url} disabled={disabledRtmpUrl} />
      )}
      <div css={labelWrapper(2)}>
        <Typography css={hint} variant="caption">
          <span>
            {intl.formatMessage(
              {
                id: "control-room-detail.rtmp-info",
              },
              {
                a: (chunk) => (
                  <a href={RTMP_URL} target="_blank" rel="noreferrer noopener" css={link}>
                    {chunk}
                  </a>
                ),
              }
            )}
          </span>
        </Typography>
      </div>
    </Group>
  );
};

export default memo(EventLinksGroup);
